/**
 * SLA policy list state machine - replaces useSlaStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { CrmSlaPolicy, SlaPriority } from '../types'

export interface SlaListContext {
  policies: CrmSlaPolicy[]
  selectedPolicyId: string | null
}

export type SlaListEvent =
  | { type: 'SET_POLICIES'; policies: CrmSlaPolicy[] }
  | { type: 'ADD_POLICY'; policy: CrmSlaPolicy }
  | { type: 'UPDATE_POLICY'; id: string; updates: Partial<CrmSlaPolicy> }
  | { type: 'DELETE_POLICY'; id: string }
  | { type: 'SET_SELECTED_POLICY_ID'; id: string | null }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as SlaListContext,
    events: {} as SlaListEvent,
  },
})

export const slaListMachine = machineSetup.createMachine({
  id: 'slaList',
  context: {
    policies: [],
    selectedPolicyId: null,
  },
  on: {
    SET_POLICIES: {
      actions: assign({
        policies: ({ event }) =>
          event.type === 'SET_POLICIES' ? event.policies : [],
      }),
    },
    ADD_POLICY: {
      actions: assign({
        policies: ({ context, event }) =>
          event.type === 'ADD_POLICY'
            ? [...context.policies, event.policy]
            : context.policies,
      }),
    },
    UPDATE_POLICY: {
      actions: assign({
        policies: ({ context, event }) => {
          if (event.type !== 'UPDATE_POLICY') return context.policies
          return context.policies.map((policy) =>
            policy.id === event.id
              ? {
                  ...policy,
                  ...event.updates,
                  updatedAt: new Date().toISOString(),
                }
              : policy
          )
        },
      }),
    },
    DELETE_POLICY: {
      actions: assign({
        policies: ({ context, event }) => {
          if (event.type !== 'DELETE_POLICY') return context.policies
          return context.policies.filter((policy) => policy.id !== event.id)
        },
      }),
    },
    SET_SELECTED_POLICY_ID: {
      actions: assign({
        selectedPolicyId: ({ event }) =>
          event.type === 'SET_SELECTED_POLICY_ID' ? event.id : null,
      }),
    },
    CLEAR: {
      actions: assign({ policies: [], selectedPolicyId: null }),
    },
  },
})
